import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'

import { useHierarchyProducts } from 'features/catalog/hooks/use-hierarchy-products'
import type { CatalogService } from 'features/catalog/service'
import type { FocusListOption } from 'features/catalog/types'
import { buildRequestPanelFromV1Filters } from 'features/charts-v2/utils'
import { usePrevFilters } from 'features/filters/hooks/use-prev-filters'
import type { FilterOption } from 'features/filters/types'
import type { Product } from 'features/products/types'
import { useProjectStore } from 'features/project/store'
import type { StatsComparisonResponse } from 'features/summary/service/types'
import type { DateWindows, Filters } from 'types/project'

export interface ChartItemShape {
  type: string
  content: { name: string; image: string; chart_ids: unknown[]; tier1: string }
  dashboardControls: Record<string, unknown>
  filterValues: {
    values: unknown[]
    searchQuery: unknown[]
    searchCondition: unknown[]
  }
}

export interface OverviewHookParams {
  catalogService: Pick<CatalogService, 'getStatsComparison'>
  projectId: string | null
  defaultFilterList: FilterOption[]
  item: FocusListOption
  filterValues: Filters
  tier1: string
  chartId: string
  // When false, all data queries are paused (kept in their loading state) so the
  // detail pane can render its skeleton before the focused item has resolved,
  // without firing requests with placeholder params. Defaults to true.
  enabled?: boolean
}

export function useOverview({
  catalogService,
  projectId,
  defaultFilterList,
  item,
  filterValues,
  tier1,
  chartId,
  enabled = true,
}: OverviewHookParams): {
  activeTier1: string
  activeValues: string[]
  setActiveTier1: React.Dispatch<React.SetStateAction<string>>
  setActiveValues: React.Dispatch<React.SetStateAction<string[]>>
  tierName: string
  isProduct: boolean
  isTheme: boolean
  isSource: boolean
  isBrand: boolean
  hierachyProductsData: Product[] | undefined
  activeFilters: Filters
  filterScopeKey: string
  chartItem: ChartItemShape
  secondaryChart: ChartItemShape | null
  projectId: string | null
  defaultFilterList: FilterOption[]
  statsComparison: StatsComparisonResponse | undefined
  comparisonLoading: boolean
  dateWindows: DateWindows
} {
  const [activeTier1, setActiveTier1] = useState<string>(tier1)
  const [activeValues, setActiveValues] = useState<string[]>(item.identifiers)

  // item.identifiers can arrive AFTER mount — a deep-linked source variant
  // resolves its identifiers only once the sidebar list loads, and the detail
  // pane does not remount (stable React key). Re-sync so the chart/summary
  // filter tracks the resolved item. The theme lens is excluded: ThemeSelector
  // owns activeValues there (subtheme drill-down).
  useEffect(() => {
    if (activeTier1 === 'theme') return
    setActiveValues((prev) =>
      prev.length === item.identifiers.length &&
      prev.every((v, i) => v === item.identifiers[i])
        ? prev
        : item.identifiers,
    )
  }, [item.identifiers, activeTier1])

  const isProduct = tier1 === 'product_hierarchy'
  const { hierachyProductsData } = useHierarchyProducts(
    isProduct && enabled ? (projectId ?? '') : '',
    isProduct && enabled ? item.value : '',
    filterValues,
  )

  const dateWindows = useProjectStore((state) => state.dateWindows)

  const tierName =
    defaultFilterList.find((el) => el.field === activeTier1)?.alias ??
    activeTier1

  const activeFilters: Filters = useMemo(() => {
    return {
      ...filterValues,
      values: [
        ...filterValues.values.filter((el) => el.field !== activeTier1),
        { field: activeTier1, values: activeValues },
      ].filter((el) => defaultFilterList.find((f) => f.field === el.field)),
    }
  }, [filterValues, activeTier1, activeValues, defaultFilterList])

  const filterScopeKey = useMemo(
    () => `${activeTier1}:${activeValues.join('|')}`,
    [activeTier1, activeValues],
  )

  const chartItem: ChartItemShape = useMemo(
    () => ({
      type: 'chart',
      content: {
        name: 'Sentiment',
        image: '4_16.png',
        chart_ids: [{ chart_id: chartId, model: null, field: null }],
        tier1: tierName,
      },
      dashboardControls: {
        chartId,
        tier1: tierName,
        tier2: null,
        tier3: null,
        legend: true,
        fullAxis: true,
        hasLabels: false,
        hasOverlay: false,
        treeView: true,
        origin: true,
        labels: false,
        trendlines: false,
        shadedZones: true,
        sortedByCategory: false,
        isAlignScales: true,
        isAlignOrder: true,
        multiChart: true,
        fontSize: 'small',
        timeInterval: null,
        reviewsCount: 0,
        postType: 'post',
        interval: null,
        isExpandChart: false,
        order: null,
        isDelta: false,
        showPercentChange: true,
        wrapLabels: false,
        maxAxesLength: 30,
      },
      filterValues: { values: [], searchQuery: [], searchCondition: [] },
    }),
    [chartId, tierName],
  )

  const secondaryChart: ChartItemShape | null = useMemo(() => {
    if (activeTier1 !== 'brand') return null
    return {
      type: 'chart',
      content: {
        name: 'Rating',
        image: '4_16.png',
        chart_ids: [{ chart_id: '9_3_19_sentiment', model: null, field: null }],
        tier1: 'Theme',
      },
      dashboardControls: {
        chartId: '9_3_19_sentiment',
        tier1: 'Theme',
        tier2: null,
        tier3: null,
        legend: true,
        fullAxis: true,
        hasLabels: false,
        hasOverlay: false,
        treeView: true,
        origin: true,
        labels: false,
        trendlines: false,
        shadedZones: true,
        sortedByCategory: false,
        isAlignScales: true,
        isAlignOrder: true,
        multiChart: true,
        fontSize: 'small',
        timeInterval: null,
        reviewsCount: 0,
        postType: 'post',
        interval: null,
        isExpandChart: false,
        order: null,
        isDelta: false,
        showCompetitors: false,
        showCategories: undefined,
        showPercentChange: true,
        wrapLabels: false,
        maxAxesLength: 30,
        matrixScaleVolume: true,
        matrixSort: true,
        matrixSortAxis: 'y',
        matrixSortValue: activeValues[0],
        matrixSpectrumColors: true,
      },
      filterValues: { values: [], searchQuery: [], searchCondition: [] },
    }
  }, [activeTier1, activeValues])

  const requestPanel = useMemo(
    () => buildRequestPanelFromV1Filters(activeFilters),
    [activeFilters],
  )

  const { dateFilter } = usePrevFilters({
    filters: activeFilters,
    dateWindows,
    defaultFilterList,
    prevIsDelta: false,
  })

  // `defaultFilterList.length > 0` avoids firing before the project's filter
  // list has loaded — `activeFilters` drops any field defaultFilterList doesn't
  // yet know about, so an early fire runs with empty/unscoped criteria, then
  // refires correctly once the list lands.
  const { data: statsComparison, isLoading: comparisonLoading } = useQuery(
    ['statsComparison', projectId, requestPanel, dateFilter],
    () =>
      catalogService.getStatsComparison(projectId ?? '', {
        panel: requestPanel,
        // Prior period: the same panel with its date range shifted back.
        prev_panel: dateFilter
          ? {
              ...requestPanel,
              dateRange: {
                startDate: dateFilter.min,
                endDate: dateFilter.max,
              },
            }
          : undefined,
        record_type: 'reviews_and_ratings',
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!projectId && enabled && defaultFilterList.length > 0,
    },
  )

  return {
    activeTier1,
    activeValues,
    setActiveTier1,
    setActiveValues,
    tierName,
    isProduct,
    isTheme: tier1 === 'theme',
    isSource: tier1 === 'source_type',
    isBrand: tier1 === 'brand',
    hierachyProductsData,
    activeFilters,
    filterScopeKey,
    chartItem,
    secondaryChart,
    projectId,
    defaultFilterList,
    statsComparison,
    comparisonLoading,
    dateWindows,
  }
}
